import genericApi from "@/api/genericApi";
import GenericApi from "@/api/genericApi";
import { GetLogsQueryKey } from "@/config/constants/QueryKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { ApiResponse } from "@/types/Api";
import { Logs } from "@/types/Logs";
import { getUser, getUserToken } from "@/utils/localStorage";
import { isBitramp } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Loader } from "lucide-react";
import React from "react";

const LogsPage: React.FC = () => {
  const { data: LogList, isLoading } = useQuery<ApiResponse<Logs[]>>({
    queryFn: async () => GenericApi.get<Logs[]>(ApiRoutes.LOGS),
    queryKey: [GetLogsQueryKey],
  });

  const user = getUser();

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  const formatHour = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        // `https://leonext.ctrl13.ro/api/statistics/logs/download`,
        `${genericApi.baseUrl}/statistics/logs/download`,
        {
          responseType: "blob",
          headers: {
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `Logs.xlsx`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading EXCEL:", error);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen mb-20">
      <h2 className="Heading2 bold dark-blue mb-4">Logs</h2>
      <div className="Container pshadow Text mb-5 tableContainer">
        <div className="flex gap-4 mb-4">
          <button
            onClick={handleDownload}
            className={`${
              isBitramp() ? "Upload_button_bitramp" : "Primary_button"
            }  w-fit mt-4 `}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="18"
              viewBox="0 0 14 18"
              fill="none"
            >
              <g clip-path="url(#clip0_3058_8281)">
                <path
                  d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
                <path
                  d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
                  fill={isBitramp() ? "#262626" : "white"}
                />
              </g>
              <defs>
                <clipPath id="clip0_3058_8281">
                  <rect width="14" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div className={`${isBitramp() ? "text-[#262626]" : ""} `}>
              Download Excel
            </div>
          </button>
        </div>
        <div
          className={`${
            isBitramp() ? "crypto-list_bitramp" : "crypto-list"
          } overflow-y-auto tableContainer--background max-h-[70vh]`}
        >
          <table className="w-full border-collapse relative">
            <thead
              className={`sticky top-0 tableContainer__header ${
                isBitramp() ? "!bg-[#EE33D9]" : "bg-[#202a43]"
              }`}
            >
              <tr>
                <th
                  className={`tableContainer__header__row text-left !ps-4 white ${
                    isBitramp() ? "!bg-[#EE33D9]" : "bg-[#202a43]"
                  }`}
                >
                  User
                </th>
                <th
                  className={`tableContainer__header__row text-left !ps-4 white ${
                    isBitramp() ? "!bg-[#EE33D9]" : "bg-[#202a43]"
                  }`}
                >
                  IP
                </th>
                <th
                  className={`tableContainer__header__row white ${
                    isBitramp() ? "!bg-[#EE33D9]" : "bg-[#202a43]"
                  }`}
                >
                  Date
                </th>
                <th
                  className={`tableContainer__header__row text-center !pe-4 white ${
                    isBitramp() ? "!bg-[#EE33D9]" : "bg-[#202a43]"
                  }`}
                >
                  Activity
                </th>
              </tr>
            </thead>
            <tbody>
              {LogList?.data?.map((log, index) => {
                return (
                  <tr key={index}>
                    <td
                      className={`text-left ps-4 py-4 w-[13%] border-b-[1px]  ${
                        isBitramp()
                          ? "text-white border-[#464646]"
                          : "border-[#E5E7EB]"
                      }`}
                    >
                      {user?.is_superuser == false
                        ? log.user.first_name
                        : "Admin"}{" "}
                      {user?.is_superuser == false ? log.user.last_name : ""}
                    </td>
                    <td
                      className={`" text-left ps-4 py-4 w-[13%] border-b-[1px]  pr-8 ${
                        isBitramp()
                          ? "text-white border-[#464646]"
                          : "border-[#E5E7EB]"
                      }`}
                    >
                      {log.ip_address}
                    </td>
                    <td
                      className={`py-4 w-[13%] border-b-[1px]  ${
                        isBitramp()
                          ? "text-white border-[#464646]"
                          : "border-[#E5E7EB]"
                      }`}
                    >
                      {formatDate(log.created_at)}{" "}
                      <span className="text-[#b3b2b1]">
                        {formatHour(log.created_at)}
                      </span>
                    </td>
                    <td
                      className={`text-right !pe-4 py-4 w-[60%] border-b-[1px] ${
                        isBitramp()
                          ? "text-white border-[#464646]"
                          : "border-[#E5E7EB]"
                      }`}
                    >
                      {log.description}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LogsPage;
